// Survives a deep link across the unlock / onboarding detour.
//
// A locked wallet redirects every guarded route to the unlock screen, which
// rewrites the URL — so `#/app-browser?dappId=…&dappUrl=…` becomes `#/unlock` and
// the parameters are gone by the time the user has typed their password. Stashing
// the intended location here lets the app return to it once the keystore opens.
//
// Everything read back out is treated as untrusted input. sessionStorage is
// same-origin but not same-code: an earlier build, another tab of this app, or a
// page-level bug can put anything in this key, and the value is fed straight into
// a navigation. So:
//   • only same-origin destinations are accepted — `startsWith('/')` is NOT
//     enough, because `//evil.com` and `/\evil.com` also start with a slash and
//     browsers resolve them as absolute URLs;
//   • the value is bounded, single-use, and scoped to this tab session.
// Whatever `dappUrl` it carries is re-validated downstream by the dApp browser
// screen (isTrustedDapp) — this module deliberately does not vouch for it.
const KEY = 'zkx_pending_deeplink'
const MAX_LENGTH = 2048

/** Routes that are the detour itself — returning to them would be a loop. */
const isDetour = (pathname: string) =>
  /^\/(unlock|get-started|auth-|keystore|onboarding)/.test(pathname)

export const savePendingDeepLink = (pathname: string, search: string) => {
  try {
    if (!pathname || isDetour(pathname)) return
    const value = `${pathname}${search || ''}`
    if (value.length > MAX_LENGTH) return
    sessionStorage.setItem(KEY, value)
  } catch {
    // Private-mode / disabled storage: the deep link is simply not restored.
  }
}

/**
 * Read and clear the stashed destination. Returns a same-origin path, or null.
 * Single-use on purpose — a value left behind would hijack the next unlock.
 */
export const consumePendingDeepLink = (): string | null => {
  let raw: string | null = null
  try {
    raw = sessionStorage.getItem(KEY)
    sessionStorage.removeItem(KEY)
  } catch {
    return null
  }
  if (!raw || raw.length > MAX_LENGTH) return null

  try {
    // Resolving against our own origin is what catches the protocol-relative and
    // backslash forms; a bare comparison of the string prefix does not.
    const resolved = new URL(raw, window.location.origin)
    if (resolved.origin !== window.location.origin) return null
    if (isDetour(resolved.pathname)) return null
    return `${resolved.pathname}${resolved.search}`
  } catch {
    return null
  }
}
